import { useState, useMemo } from "react";
import { Search, Copy, ExternalLink, TrendingUp, Grid, FolderOpen, FileWarning, LogIn, AlertTriangle, Router, Database, Server, FileSearch, Shield, Star, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { ScrollArea } from "@/components/ui/scroll-area";
import { popularDorks, dorkCategories, getDorksByCategory, CatalogDork } from "@/data/popularDorks";
import { toast } from "@/hooks/use-toast";
import { useLocale } from "@/contexts/LocaleContext";

const iconMap: Record<string, React.ComponentType<{ className?: string }>> = {
  Grid, FolderOpen, FileWarning, LogIn, AlertTriangle, Router, Database, Server, FileSearch, Search, Shield,
};

interface DorkCatalogProps {
  onSelectDork?: (query: string) => void;
}

export const DorkCatalog = ({ onSelectDork }: DorkCatalogProps) => {
  const { t } = useLocale();
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const filteredDorks = useMemo(() => {
    let dorks = getDorksByCategory(selectedCategory);
    
    if (searchQuery.trim()) {
      const terms = searchQuery.toLowerCase().split(/\s+/);
      dorks = dorks.filter(dork => {
        const searchableText = `${dork.title} ${dork.description} ${dork.query} ${dork.tags.join(' ')}`.toLowerCase();
        return terms.every(term => searchableText.includes(term));
      });
    }
    
    return dorks.sort((a, b) => b.popularity - a.popularity);
  }, [searchQuery, selectedCategory]);

  const handleCopy = async (dork: CatalogDork) => {
    await navigator.clipboard.writeText(dork.query);
    setCopiedId(dork.id);
    toast({
      title: t("catalog.copied"),
      description: dork.title,
    });
    setTimeout(() => setCopiedId(null), 2000);
  };

  const handleSearch = (dork: CatalogDork) => {
    window.open(`https://www.google.com/search?q=${encodeURIComponent(dork.query)}`, '_blank');
  };

  const handleUse = (dork: CatalogDork) => {
    onSelectDork?.(dork.query);
    toast({
      title: t("catalog.loaded"),
      description: t("catalog.loaded_desc"),
    });
  };

  const getCategoryColor = (category: string) => {
    const colors: Record<string, string> = {
      'sensitive-directories': 'bg-orange-500/10 text-orange-500 border-orange-500/20',
      'sensitive-files': 'bg-red-500/10 text-red-500 border-red-500/20',
      'login-pages': 'bg-blue-500/10 text-blue-500 border-blue-500/20',
      'error-messages': 'bg-yellow-500/10 text-yellow-500 border-yellow-500/20',
      'network-devices': 'bg-purple-500/10 text-purple-500 border-purple-500/20',
      'databases': 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20',
      'servers': 'bg-cyan-500/10 text-cyan-500 border-cyan-500/20',
      'file-discovery': 'bg-pink-500/10 text-pink-500 border-pink-500/20',
      'osint': 'bg-indigo-500/10 text-indigo-500 border-indigo-500/20',
      'vulnerabilities': 'bg-rose-500/10 text-rose-500 border-rose-500/20',
    };
    return colors[category] || 'bg-muted text-muted-foreground';
  };

  return (
    <div className="glass-card p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary to-secondary flex items-center justify-center">
            <TrendingUp className="w-5 h-5 text-primary-foreground" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-foreground">{t("catalog.title")}</h3>
            <p className="text-sm text-muted-foreground">{t("catalog.subtitle", { count: String(popularDorks.length) })}</p>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder={t("catalog.search_placeholder")}
          className="pl-10 bg-muted/50 border-muted-foreground/20"
        />
      </div>

      {/* Category Tabs */}
      <div className="overflow-x-auto scrollbar-hide">
        <div className="inline-flex h-auto p-1 bg-muted/50 rounded-lg" role="tablist">
          {dorkCategories.map(category => {
            const IconComponent = iconMap[category.icon] || Grid;
            const isActive = selectedCategory === category.id;
            return (
              <button
                key={category.id}
                role="tab"
                aria-selected={isActive}
                onClick={() => setSelectedCategory(category.id)}
                className={cn(
                  "flex items-center gap-2 px-3 py-2 text-xs whitespace-nowrap rounded-sm font-medium transition-all",
                  isActive
                    ? "bg-primary text-primary-foreground shadow-sm"
                    : "text-muted-foreground hover:text-foreground"
                )}
              >
                <IconComponent className="w-3.5 h-3.5" />
                {category.name}
              </button>
            );
          })}
        </div>
      </div>

      {/* Results Count */}
      <div className="text-sm text-muted-foreground">
        {filteredDorks.length === 1
          ? t("catalog.showing", { count: String(filteredDorks.length) })
          : t("catalog.showing_plural", { count: String(filteredDorks.length) })}
        {selectedCategory !== 'all' && t("catalog.in_category", { category: dorkCategories.find(c => c.id === selectedCategory)?.name ?? selectedCategory })}
      </div>

      {/* Dork List */}
      <ScrollArea className="h-[500px] pr-4">
        <div className="space-y-3">
          {filteredDorks.map((dork) => (
            <div
              key={dork.id}
              className="group p-4 rounded-xl bg-muted/30 border border-muted-foreground/10 hover:border-primary/30 hover:bg-muted/50 transition-all duration-200"
            >
              <div className="flex items-start justify-between gap-4 mb-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h4 className="font-medium text-foreground truncate">{dork.title}</h4>
                    <div className="flex items-center gap-1">
                      <Star className="w-3 h-3 text-yellow-500 fill-yellow-500" />
                      <span className="text-xs text-muted-foreground">{dork.popularity}</span>
                    </div>
                  </div>
                  <p className="text-sm text-muted-foreground line-clamp-2">{dork.description}</p>
                </div>
                <Badge className={`${getCategoryColor(dork.category)} text-xs shrink-0`}>
                  {dork.category.replace('-', ' ')}
                </Badge>
              </div>

              <div className="mb-3 p-3 rounded-lg bg-background/50 border border-muted-foreground/10 font-mono text-sm text-foreground break-all">
                {dork.query}
              </div>

              <div className="flex flex-wrap gap-1.5 mb-3">
                {dork.tags.slice(0, 5).map(tag => (
                  <span key={tag} className="px-2 py-0.5 rounded-full bg-muted text-xs text-muted-foreground">
                    {tag}
                  </span>
                ))}
                {dork.source && (
                  <span className="px-2 py-0.5 rounded-full bg-primary/10 text-xs text-primary font-medium">
                    {dork.source}
                  </span>
                )}
              </div>

              <div className="flex items-center gap-2">
                <Button size="sm" variant="ghost" onClick={() => handleCopy(dork)} className="h-8 px-3 text-xs">
                  {copiedId === dork.id ? (
                    <Check className="w-3.5 h-3.5 mr-1.5 text-green-500" />
                  ) : (
                    <Copy className="w-3.5 h-3.5 mr-1.5" />
                  )}
                  {t("catalog.copy")}
                </Button>
                <Button size="sm" variant="ghost" onClick={() => handleSearch(dork)} className="h-8 px-3 text-xs">
                  <ExternalLink className="w-3.5 h-3.5 mr-1.5" />
                  {t("catalog.search")}
                </Button>
                {onSelectDork && (
                  <Button size="sm" variant="secondary" onClick={() => handleUse(dork)} className="h-8 px-3 text-xs">
                    {t("catalog.use_query")}
                  </Button>
                )}
              </div>
            </div>
          ))}

          {filteredDorks.length === 0 && (
            <div className="text-center py-12 text-muted-foreground">
              <Search className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p className="font-medium">{t("catalog.no_results")}</p>
              <p className="text-sm">{t("catalog.no_results_desc")}</p>
            </div>
          )}
        </div>
      </ScrollArea>
    </div>
  );
};
